<script setup lang="ts">
  import { type PropType } from 'vue';

  interface BaseEntity {
    id: number;
    name: string;
    country_code: string;
    first_line: string;
    second_line: string | null;
    third_line: string;
    vat_id: string | null;
    email: string;
    phone: string | null;
  }

  interface Manufacturer extends BaseEntity {

  }

  interface Entity extends BaseEntity {

  }

  interface Importer extends BaseEntity {

  }

  interface SafetyNote {
    id: string;
    description: string;
  }

  interface Product {
    id: number,
    variant_id: number,
    photo: string | null,
    title: string,
    sku: string | null,
    reviews_count: number,
    reviews_average: number | string,
    responsible_manufacturer: Manufacturer | null,
    responsible_entity: Entity | null,
    product_importers: Importer[],
    safety_notes: SafetyNote[],
  }

  const props = defineProps({
    product: { type: Object as PropType<Product>, required: true },
  });
</script>
<template>
  <div class="product-safety">
    <div v-if="product.safety_notes.length > 0" class="notes">
      <ul>
        <li v-for="note in product.safety_notes" v-html="note.description" />
      </ul>
    </div>
    <div class="entities">
      <div v-if="product.responsible_manufacturer" class="entity">
        <p class="label">{{ $t('Producent:') }}</p>
        <h3>{{ product.responsible_manufacturer.name }}</h3>
        <SfAddressInline :segments="[product.responsible_manufacturer.first_line, product.responsible_manufacturer.second_line, product.responsible_manufacturer.third_line, product.responsible_manufacturer.country_code]" />
        <p v-if="product.responsible_manufacturer.email">
          <a :href="`mailto:${product.responsible_manufacturer.email}`" target="_blank" v-html="product.responsible_manufacturer.email" />
        </p>
        <p v-if="product.responsible_manufacturer.phone">
          <a :href="`tel:${product.responsible_manufacturer.phone}`" target="_blank" v-html="product.responsible_manufacturer.phone" />
        </p>
      </div>
      <div v-if="product.responsible_entity" class="entity">
        <p class="label">{{ $t('Podmiot odpowiedzialny:') }}</p>
        <h3>{{ product.responsible_entity.name }}</h3>
        <SfAddressInline :segments="[product.responsible_entity.first_line, product.responsible_entity.second_line, product.responsible_entity.third_line, product.responsible_entity.country_code]" />
        <p v-if="product.responsible_entity.email">
          <a :href="`mailto:${product.responsible_entity.email}`" target="_blank" v-html="product.responsible_entity.email" />
        </p>
        <p v-if="product.responsible_entity.phone">
          <a :href="`tel:${product.responsible_entity.phone}`" target="_blank" v-html="product.responsible_entity.phone" />
        </p>
      </div>
      <div v-for="importer in product.product_importers" class="entity">
        <p class="label">{{ $t('Importer:') }}</p>
        <h3>{{ importer.name }}</h3>
        <SfAddressInline :segments="[importer.first_line, importer.second_line, importer.third_line, importer.country_code]" />
        <p v-if="importer.email">
          <a :href="`mailto:${importer.email}`" target="_blank" v-html="importer.email" />
        </p>
        <p v-if="importer.phone">
          <a :href="`tel:${importer.phone}`" target="_blank" v-html="importer.phone" />
        </p>
      </div>
    </div>
  </div>
</template>
<style scoped lang="scss">
  .product-safety {
    display: flex;
    flex-direction: column;
    gap: 2rem;

    .notes {
      ul {
        margin: 0 0 0 1.2rem;
        padding: 0;
      }
    }

    .entities {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
      gap: 1rem;

      .entity {
        font-size: 0.875rem;

        .label {
          font-weight: var(--font-weight-bold);
          font-size: 0.75rem;
        }

        h3 {
          font-weight: var(--font-weight-regular);
          font-size: 0.875rem;
        }

        address {
          font-style: normal;
        }
      }
    }
  }
</style>
